import { typography as designTypography } from "../../../app/design-system";
import { renderPaginationRange } from "../../../app/utils/paginationRange";
import { CreditCardOutlined,EditOutlined,FilterOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { useQueryClient } from "@tanstack/react-query";
import { Button,Flex,Form,Grid,Switch,Tag,Typography } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import {
  CONTRIBUTION_STATUS_CANCELLED,
  CONTRIBUTION_STATUS_DUE,
  CONTRIBUTION_STATUS_IN_DEBT_COLLECTION,
  CONTRIBUTION_STATUS_OVERDUE,
  CONTRIBUTION_STATUS_PAID,
  CONTRIBUTION_STATUS_PENDING,
  CONTRIBUTION_STATUS_RESERVED
} from "../../../app/constants/contributionCalendarStatus";
import type { LocalizedText } from "../../../app/i18n/i18n";
import { formatCycleUnitLabel } from "../../../app/utils/cycleUnitI18n";
import { paymentMethodLabel } from "../../../app/utils/paymentMethod";
import { reminderCollectionStatusKey } from "../../../app/utils/reminderAction";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import type { useProTableSortPersistence } from "../../../app/utils/proTableSortPersistence";
import { formatDateList,formatDateOnly,formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import type { PaymentItem } from "../../../components/PaymentDrawer";
import { FormDrawer } from "../../../components/FormDrawer";
import type { ContributionBulkPayFormValues } from "./PersonContributionBulkPayDrawer";
import type { ContributionEditFormValues } from "./PersonContributionEditDrawer";
import type { ContributionCalendarRow } from "./personDetailTypes";
import {
  contributionCycleUnitToDayjsPeriodUnit
} from "./personDetailUtils";
import { personDetailQueryKeys } from "./personDetailQueryKeys";
import {
  isContributionEditable,
  isContributionSelectable
} from "./personContributionEligibility";
import { ContributionCancellationAmount, OpenItemCancellationAction } from "../../../components/OpenItemCancellationAction";
import { PersonContributionOpenItemActions } from "./PersonContributionOpenItemActions";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { CONTRIBUTION_AMOUNT_TOLERANCE, STORED_BOOLEAN_TRUE } from "./personDetailValues";

const CONTRIBUTIONS_PAGE_SIZE = 20;

const USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH = {
  dueDate: 140,
  contractName: 200,
  amount: 160,
  paidAmount: 140,
  openAmount: 140,
  paymentMethod: 200,
  paidDates: 160,
  billingCycle: 100,
  status: 120,
  actions: 200
} as const;

const CONTRIBUTION_ENTRY_TYPE_REST_PERIOD_FEE = 3;
const CONTRIBUTION_ENTRY_TYPE_CHARGEBACK_FEE = 6;

type ContributionSortController = {
  sortRows: (
    rows: ContributionCalendarRow[],
    requestSort?: Record<string, "ascend" | "descend">
  ) => ContributionCalendarRow[];
  /** Signatur direkt von der Quelle abgeleitet, damit sie nicht auseinanderlaeuft. */
  onTableChange: ReturnType<typeof useProTableSortPersistence>["onTableChange"];
};

type UsePersonContributionsControllerArgs = {
  api: ApiClient;
  personId: string;
  accountId?: string | null;
  reloadFinanceRows: () => void;
  canWriteFinance: boolean;
  canAgreeInstallment: boolean;
  dateInputFormat: string | string[];
  text: (value: LocalizedText) => string;
  units: UnitsSettings;
  sortContributions: ContributionSortController;
};

function contributionDisplayPaidAmount(row: ContributionCalendarRow): number {
  if (Number(row.amount || 0) < -CONTRIBUTION_AMOUNT_TOLERANCE) return 0;
  return Number(row.paidAmount || 0);
}

function contributionDisplayOpenAmount(row: ContributionCalendarRow): number {
  if (Number(row.amount || 0) < -CONTRIBUTION_AMOUNT_TOLERANCE || Number(row.openAmount || 0) < -CONTRIBUTION_AMOUNT_TOLERANCE) return 0;
  return Number(row.openAmount || 0);
}

function isContributionPayable(row: ContributionCalendarRow): boolean {
  return isContributionSelectable(row) && Number(row.openAmount || 0) > CONTRIBUTION_AMOUNT_TOLERANCE;
}

export function usePersonContributionsController({
  api,
  personId,
  accountId,
  reloadFinanceRows,
  canWriteFinance,
  canAgreeInstallment,
  dateInputFormat,
  text,
  units,
  sortContributions
}: UsePersonContributionsControllerArgs) {
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const useStackedToolbar = screens.sm === false;
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const [edit, setEdit] = React.useState<ContributionCalendarRow | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editSaving, setEditSaving] = React.useState(false);
  const [editForm] = Form.useForm<ContributionEditFormValues>();

  const [paymentDrawerOpen, setPaymentDrawerOpen] = React.useState(false);
  const [paymentDrawerItems, setPaymentDrawerItems] = React.useState<PaymentItem[]>([]);
  const [paymentDrawerSelectedItems, setPaymentDrawerSelectedItems] = React.useState<PaymentItem[]>([]);
  const [selectedRows, setSelectedRows] = React.useState<ContributionCalendarRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);

  const [bulkPayOpen, setBulkPayOpen] = React.useState(false);
  const [bulkPaySaving, setBulkPaySaving] = React.useState(false);
  const [bulkPayForm] = Form.useForm<ContributionBulkPayFormValues>();
  const [bulkPayContributions, setBulkPayContributions] = React.useState<ContributionCalendarRow[]>([]);

  const pruneSelectedContributions = React.useCallback((rows: ContributionCalendarRow[]) => {
    const selectableRowsById = new Map(
      rows
        .filter(isContributionSelectable)
        .map((row) => [String(row.id), row])
    );
    setSelectedRowKeys((previousKeys) => {
      const nextKeys = previousKeys.filter((key) => selectableRowsById.has(String(key)));
      if (nextKeys.length === previousKeys.length) return previousKeys;
      return nextKeys;
    });
    setSelectedRows((previousRows) => {
      const nextRows = previousRows
        .map((row) => selectableRowsById.get(String(row.id)))
        .filter((row): row is ContributionCalendarRow => Boolean(row));
      if (
        nextRows.length === previousRows.length
        && nextRows.every((row, index) => row === previousRows[index])
      ) {
        return previousRows;
      }
      return nextRows;
    });
  }, []);

  const storageAccountId = accountId || "default";
  const storageKeyShowHistorical = `persons-detail-contributions-showHistorical-${storageAccountId}`;
  const storageKeyShowFuture = `persons-detail-contributions-showFuture-${storageAccountId}`;
  const [showHistorical, setShowHistorical] = React.useState<boolean>(() => {
    const saved = localStorage.getItem(storageKeyShowHistorical);
    return saved === null ? true : saved === STORED_BOOLEAN_TRUE;
  });
  const [showFuture, setShowFuture] = React.useState<boolean>(() => {
    const saved = localStorage.getItem(storageKeyShowFuture);
    return saved === null ? true : saved === STORED_BOOLEAN_TRUE;
  });

  React.useEffect(() => {
    localStorage.setItem(storageKeyShowHistorical, String(showHistorical));
  }, [showHistorical, storageKeyShowHistorical]);

  React.useEffect(() => {
    localStorage.setItem(storageKeyShowFuture, String(showFuture));
  }, [showFuture, storageKeyShowFuture]);

  React.useEffect(() => {
    reloadFinanceRows();
  }, [reloadFinanceRows, showHistorical, showFuture]);

  const openEdit = React.useCallback(
    (row: ContributionCalendarRow) => {
      setEdit(row);
      editForm.setFieldsValue({
        amount: Number(row.amount || 0),
        paymentMethod: Number(row.paymentMethod || 0)
      });
      setEditOpen(true);
    },
    [editForm]
  );

  const closeEdit = React.useCallback(() => {
    setEditOpen(false);
    setEdit(null);
    editForm.resetFields();
  }, [editForm]);

  const openPaymentForSelectedRows = React.useCallback(() => {
    const payables = selectedRows.filter(isContributionPayable);
    const items: PaymentItem[] = payables.map((row) => ({
      id: row.openItemId || row.id,
      openItemId: row.openItemId || null,
      description:
        String(row.contractName || row.contractPlanName || "").trim()
        || `${text("Contribution")} ${formatDateOnly(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH), units)}`,
      dueDate: String(row.dueDate || "").slice(0, ISO_DATE_LENGTH),
      openAmount: Number(row.openAmount || 0),
      paymentMethod: row.paymentMethod ?? null,
      type: "contribution"
    }));
    setPaymentDrawerItems(items);
    setPaymentDrawerSelectedItems(items);
    setPaymentDrawerOpen(true);
  }, [selectedRows, text, units]);

  const columnDefinitions = React.useMemo<ColumnDefinition[]>(
    () => [
      { key: "dueDate", label: text("Due date"), defaultVisible: true },
      { key: "contractName", label: text("Contract component"), defaultVisible: true },
      { key: "amount", label: text("Contribution"), defaultVisible: true },
      { key: "paidAmount", label: text("Paid"), defaultVisible: true },
      { key: "openAmount", label: text("Open"), defaultVisible: true },
      { key: "paidDates", label: text("Payment date"), defaultVisible: true },
      { key: "paymentMethod", label: text("Payment method"), defaultVisible: true },
      { key: "billingCycle", label: text("Cycle"), defaultVisible: true },
      { key: "status", label: text("Status"), defaultVisible: true }
    ],
    [text]
  );

  const cycleUnitLabel = React.useCallback((value?: number | null) => formatCycleUnitLabel(value, text), [text]);

  const allColumnsMap = React.useMemo<Record<string, ListColumn<ContributionCalendarRow>>>(() => ({
    dueDate: {
      title: text("Due date"),
      dataIndex: "dueDate",
      key: "dueDate",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.dueDate,
      defaultSortOrder: "descend",
      sorter: (a, b) => {
        const aDate = dayjs(String(a.dueDate || "").slice(0, ISO_DATE_LENGTH));
        const bDate = dayjs(String(b.dueDate || "").slice(0, ISO_DATE_LENGTH));
        if (!aDate.isValid() && !bDate.isValid()) return 0;
        if (!aDate.isValid()) return 1;
        if (!bDate.isValid()) return -1;
        if (aDate.isBefore(bDate)) return -1;
        if (aDate.isAfter(bDate)) return 1;
        return String(a.bookedAt || "").localeCompare(String(b.bookedAt || ""));
      },
      render: (_, row) => {
        const raw = String(row.dueDate || "").slice(0, ISO_DATE_LENGTH);
        const label = raw ? formatDateOnly(raw, units) : "—";
        return <span>{label}</span>;
      }
    },
    contractName: {
      title: text("Contract component"),
      dataIndex: "contractName",
      key: "contractName",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.contractName,
      ellipsis: false,
      sorter: (a, b) => String(a.contractName || "").trim().localeCompare(String(b.contractName || "").trim())
        || String(a.periodDescription || "").localeCompare(String(b.periodDescription || "")),
      render: (_, row) => <Flex vertical gap={2}>
        <span>{String(row.contractName || "").trim() || "—"}</span>
        {row.periodDescription && <Typography.Text type="secondary">{row.periodDescription}</Typography.Text>}
      </Flex>
    },
    amount: {
      title: text("Contribution"),
      dataIndex: "amount",
      key: "amount",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.amount,
      align: "right",
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
      render: (_, row) => <ContributionCancellationAmount amount={Number(row.amount || 0)} cancelledAmount={row.cancelledAmount} />
    },
    paidAmount: {
      title: text("Paid (status)"),
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.paidAmount,
      align: "right",
      sorter: (a, b) => Number(a.paidAmount || 0) - Number(b.paidAmount || 0),
      render: (_, row) => formatMoney(contributionDisplayPaidAmount(row), units)
    },
    openAmount: {
      title: text("Open (status)"),
      dataIndex: "openAmount",
      key: "openAmount",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.openAmount,
      align: "right",
      sorter: (a, b) => Number(a.openAmount || 0) - Number(b.openAmount || 0),
      render: (_, row) => formatMoney(contributionDisplayOpenAmount(row), units)
    },
    paymentMethod: {
      title: text("Payment method"),
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.paymentMethod,
      sorter: (a, b) => {
        const left = a.debtCollectionAgencyName
          ? text("Debt collection with agency").replace("{0}", String(a.debtCollectionAgencyName))
          : paymentMethodLabel(Number(a.paymentMethod || 0));
        const right = b.debtCollectionAgencyName
          ? text("Debt collection with agency").replace("{0}", String(b.debtCollectionAgencyName))
          : paymentMethodLabel(Number(b.paymentMethod || 0));
        return left.localeCompare(right);
      },
      render: (_, row) => {
        if (row.debtCollectionAgencyName) {
          return text("Debt collection with agency").replace("{0}", String(row.debtCollectionAgencyName));
        }
        if (typeof row.amount === "number" && row.amount < 0) {
          return text("Rebooking");
        }
        return paymentMethodLabel(Number(row.paymentMethod || 0));
      }
    },
    paidDates: {
      title: text("Payment date"),
      dataIndex: "paymentDates",
      key: "paidDates",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.paidDates,
      render: (_, row) => {
        const dates = (row.paymentDates || []).filter((date) => date);
        if (dates.length === 0 && Number(row.status || 0) === CONTRIBUTION_STATUS_RESERVED && row.submittedSepaExecutionDate) {
          return formatDateOnly(String(row.submittedSepaExecutionDate).slice(0, ISO_DATE_LENGTH), units);
        }
        return formatDateList(dates.map((date) => String(date).slice(0, ISO_DATE_LENGTH)), units, "—");
      }
    },
    billingCycle: {
      title: text("Cycle"),
      dataIndex: "billingCycle",
      key: "billingCycle",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.billingCycle,
      sorter: (a, b) => Number(a.billingCycle || 0) - Number(b.billingCycle || 0),
      render: (_, row) => {
        if (typeof row.amount === "number" && row.amount < 0) return null;
        if (Number(row.entryType || 0) === CONTRIBUTION_ENTRY_TYPE_REST_PERIOD_FEE) return null;
        if (Number(row.entryType || 0) === CONTRIBUTION_ENTRY_TYPE_CHARGEBACK_FEE) return null;
        const isInstallment = row.note && row.note.includes("|installment-");
        if (isInstallment && row.paymentPlanCycleUnit != null) {
          return cycleUnitLabel(row.paymentPlanCycleUnit);
        }
        const cycle = Number(row.billingCycle || 0);
        return formatCycleUnitLabel(cycle, text) || String(cycle);
      }
    },
    status: {
      title: text("Status"),
      dataIndex: "status",
      key: "status",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.status,
      sorter: (a, b) => Number(a.retryCollection?.status ?? a.status) - Number(b.retryCollection?.status ?? b.status),
      render: (_, row) => {
        if (row.retryCollection && row.openAmount > CONTRIBUTION_AMOUNT_TOLERANCE) return <Flex vertical gap={2}>
          <Tag color="blue">{text(reminderCollectionStatusKey(row.retryCollection.status))}</Tag>
          <Typography.Text type="secondary">{formatDateOnly(row.retryCollection.collectionDate, units)}</Typography.Text>
        </Flex>;
        if (typeof row.amount === "number" && row.amount < 0) return <Tag color="cyan">{text("Applied correction")}</Tag>;
        const rawStatus = Number(row.status || 0);
        if (rawStatus === CONTRIBUTION_STATUS_CANCELLED) {
          if (row.isManual && !row.cancelledAmount) {
            return <Tag color="default">{text("Contribution manually waived")}</Tag>;
          }
          return <Tag color="red">{text("Open item status cancelled")}</Tag>;
        }
        if (Number(row.amount ?? 0) <= CONTRIBUTION_AMOUNT_TOLERANCE) return <Tag>{text("—")}</Tag>;
        const rawDueDate = String(row.dueDate || "").slice(0, ISO_DATE_LENGTH);
        const dueDate = rawDueDate ? dayjs(rawDueDate) : null;
        const isFuture = dueDate && dueDate.isValid() && dueDate.isAfter(dayjs().startOf("day"), "day");
        if (rawStatus === CONTRIBUTION_STATUS_PENDING) {
          if (row.isRenewalPeriod === true) {
            return <Tag color="gold">{text("Scheduled")}</Tag>;
          }
          return <Tag color="blue">{isFuture ? text("Scheduled") : text("Pending")}</Tag>;
        }
        if (rawStatus === CONTRIBUTION_STATUS_DUE) return <Tag color="orange">{text("Due")}</Tag>;
        if (rawStatus === CONTRIBUTION_STATUS_OVERDUE) return <Tag color="red">{text("Reminder eligible (status)")}</Tag>;
        if (rawStatus === CONTRIBUTION_STATUS_PAID) return <Tag color="green">{text("Paid (status)")}</Tag>;
        if (rawStatus === CONTRIBUTION_STATUS_RESERVED) return <Tag color="purple">{text("Direct debit submitted")}</Tag>;
        if (rawStatus === CONTRIBUTION_STATUS_IN_DEBT_COLLECTION) return <Tag color="purple">{text("Contribution calendar status debt collection")}</Tag>;
        return <Tag>{String(row.status ?? "")}</Tag>;
      }
    }
  }), [cycleUnitLabel, text, units]);

  const actionsColumn = React.useMemo<ListColumn<ContributionCalendarRow>>(
    () => ({
      title: "",
      key: "actions",
      width: USE_PERSON_CONTRIBUTIONS_CONTROLLER_COLUMN_WIDTH.actions,
      sorter: false,
      fixed: "right",
      align: "right",
      render: (_, row) => {
        if (!canWriteFinance || !isContributionEditable(row)) return null;
        return (
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            {text("Edit")}
          </Button>
        );
      }
    }),
    [canWriteFinance, openEdit, text]
  );

  const rowSelection = React.useMemo(() => ({
    selectedRowKeys,
    onChange: (nextSelectedRowKeys: React.Key[], nextSelectedRows: ContributionCalendarRow[]) => {
      setSelectedRowKeys(nextSelectedRowKeys);
      setSelectedRows(nextSelectedRows);
    },
    // Checkbox-Spalte sichtbar inkl. „alle markieren"; Zeilenklick wählt zusätzlich.
    hideSelectAll: false,
    alwaysShowAlert: false,
    getCheckboxProps: (record: ContributionCalendarRow) => {
      const isReserved = Number(record.status || 0) === CONTRIBUTION_STATUS_RESERVED;
      const isInDebtCollection = Number(record.status || 0) === CONTRIBUTION_STATUS_IN_DEBT_COLLECTION;
      const isPaid = Number(record.status || 0) === CONTRIBUTION_STATUS_PAID;
      return {
        disabled: !isContributionSelectable(record),
        title: isReserved
          ? text("Contribution is already in a SEPA direct debit run")
          : isInDebtCollection
            ? text("Contribution calendar status debt collection")
            : isPaid
              ? text("Contribution is already fully paid")
              : undefined
      };
    }
  }), [selectedRowKeys, text]);

  const onRowClickForSelection = React.useCallback(
    (record: ContributionCalendarRow, event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest("button") || target.closest("a") || target.closest(".ck-btn")) return;
      // Klick auf die Checkbox/Auswahlspalte nicht zusätzlich toggeln (sonst Doppel-Toggle).
      if (target.closest(".ck-checkbox-wrapper") || target.closest("input[type=\"checkbox\"]")) return;
      if (!isContributionSelectable(record)) return;
      const currentKeys = [...selectedRowKeys];
      const keyIndex = currentKeys.indexOf(record.id);
      if (keyIndex > -1) {
        currentKeys.splice(keyIndex, 1);
        setSelectedRowKeys(currentKeys);
        setSelectedRows((previous) => previous.filter((row) => row.id !== record.id));
      } else {
        setSelectedRowKeys([...currentKeys, record.id]);
        setSelectedRows((previous) => [...previous, record]);
      }
    },
    [selectedRowKeys]
  );

  const pagination = React.useMemo(() => ({
    pageSize: CONTRIBUTIONS_PAGE_SIZE,
    showTotal: (total: number, range: [number, number]) => {
      if (selectedRows.length === 0) {
        return renderPaginationRange(text, total, range);
      }
      const totalAmount = selectedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
      const totalPaid = selectedRows.reduce((sum, row) => sum + contributionDisplayPaidAmount(row), 0);
      const totalOpen = selectedRows.reduce((sum, row) => sum + contributionDisplayOpenAmount(row), 0);
      return (
        <Flex align="center" gap={16} wrap="nowrap" justify="flex-start" style={{ width: "100%" }}>
          <Flex align="center" gap={16} wrap="nowrap">
            <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.compact, whiteSpace: "nowrap" }}>
              {text("Contribution")}: <Typography.Text strong style={{ fontSize: designTypography.fontSize.compact }}>{formatMoney(totalAmount, units)}</Typography.Text>
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.compact, whiteSpace: "nowrap" }}>
              {text("Paid")}: <Typography.Text strong style={{ fontSize: designTypography.fontSize.compact }}>{formatMoney(totalPaid, units)}</Typography.Text>
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.compact, whiteSpace: "nowrap" }}>
              {text("Open (status)")}: <Typography.Text strong style={{ fontSize: designTypography.fontSize.compact }}>{formatMoney(totalOpen, units)}</Typography.Text>
            </Typography.Text>
          </Flex>
          <div style={{ flex: 1 }} />
          {renderPaginationRange(text, total, range)}
        </Flex>
      );
    }
  }), [selectedRows, text, units]);

  const headerExtra = React.useMemo(
    () => {
      const filterControls = (
        <>
          <Flex gap={8} align="center" justify={useStackedToolbar ? "space-between" : "flex-end"} style={{ minWidth: 0, width: useStackedToolbar ? "100%" : undefined }}>
            <Typography.Text>{text("Historical contributions")}</Typography.Text>
            <Switch checked={showHistorical} onChange={setShowHistorical} />
          </Flex>
          <Flex gap={8} align="center" justify={useStackedToolbar ? "space-between" : "flex-end"} style={{ minWidth: 0, width: useStackedToolbar ? "100%" : undefined }}>
            <Typography.Text>{text("Future contributions")}</Typography.Text>
            <Switch checked={showFuture} onChange={setShowFuture} />
          </Flex>
        </>
      );
      return (
      <Flex wrap="wrap" gap={12} align="center" justify="flex-end" style={{ width: "100%", minWidth: 0 }}>
        {useStackedToolbar ? (
          <>
            <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
              {text("Filter")}
            </Button>
            <FormDrawer dirty={false} hideCancel
              title="Filter"
              open={filterDrawerOpen}
              onCancel={() => setFilterDrawerOpen(false)}
              hideSubmit
              cancelText="Close"
              autoFocusFirstField={false}
            >
              <Flex vertical gap={12} style={{ width: "100%" }}>
                {filterControls}
              </Flex>
            </FormDrawer>
          </>
        ) : filterControls}
        <OpenItemCancellationAction personId={personId} selectedOpenItemIds={selectedRows.flatMap((row) => row.openItemId ? [row.openItemId] : [])} onChanged={reloadFinanceRows} />
        {canAgreeInstallment || canWriteFinance ? (
          <PersonContributionOpenItemActions
            api={api}
            personId={personId}
            text={text}
            units={units}
            dateInputFormat={dateInputFormat}
            selectedRows={selectedRows}
            canAgreeInstallment={canAgreeInstallment}
            canWriteFinance={canWriteFinance}
            onChanged={reloadFinanceRows}
          />
        ) : null}
        {canWriteFinance ? (
          <Button type="primary" icon={<CreditCardOutlined />} onClick={openPaymentForSelectedRows}>
            {text("Pay contributions")}
          </Button>
        ) : null}
      </Flex>
    );
    },
    [api, canAgreeInstallment, canWriteFinance, dateInputFormat, filterDrawerOpen, openPaymentForSelectedRows, personId, reloadFinanceRows, selectedRows, showFuture, showHistorical, text, units, useStackedToolbar]
  );

  const request = React.useCallback(
    async (_params: unknown, requestSort?: Record<string, "ascend" | "descend">) => {
      const list = await queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.person.contributions(personId),
        queryFn: () => api.get<ContributionCalendarRow[]>(`/api/v1/contribution-calendar?personId=${encodeURIComponent(personId)}`),
        staleTime: 0
      });
      const today = dayjs().startOf("day");
      let filtered = list || [];
      if (!showHistorical) {
        filtered = filtered.filter((row) => {
          const dueDate = dayjs(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH));
          const periodUnit = contributionCycleUnitToDayjsPeriodUnit(row.billingCycle);
          const isHistorical = dueDate.isValid() && dueDate.isBefore(today, periodUnit);
          if (isHistorical && Number(row.openAmount || 0) > 0) {
            return true;
          }
          return !isHistorical;
        });
      }
      if (!showFuture) {
        filtered = filtered.filter((row) => {
          const dueDate = dayjs(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH));
          return dueDate.isValid() && !dueDate.isAfter(today, "day");
        });
      }

      if (bulkPayOpen) {
        const monthRange = bulkPayForm.getFieldValue("months");
        if (monthRange && monthRange[0] && monthRange[1]) {
          const startMonth = dayjs(monthRange[0]).startOf("month");
          const endMonth = dayjs(monthRange[1]).endOf("month");
          const matchingContributions = filtered.filter((row) => {
            const dueDate = dayjs(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH));
            if (!dueDate.isValid()) return false;
            return !dueDate.isBefore(startMonth, "month")
              && !dueDate.isAfter(endMonth, "month")
              && Number(row.openAmount || 0) > 0
              && Number(row.status || 0) !== CONTRIBUTION_STATUS_CANCELLED;
          });
          setBulkPayContributions(matchingContributions);
          const totalAmount = matchingContributions.reduce((sum, row) => sum + Number(row.openAmount || 0), 0);
          const currentAmount = bulkPayForm.getFieldValue("amount");
          if (!currentAmount || currentAmount === 0) {
            bulkPayForm.setFieldsValue({ amount: totalAmount });
          }
        }
      }

      const sorted = sortContributions.sortRows(filtered, requestSort);
      pruneSelectedContributions(sorted);
      return { data: sorted, success: true, total: sorted.length };
    },
    [api, bulkPayForm, bulkPayOpen, personId, pruneSelectedContributions, queryClient, showFuture, showHistorical, sortContributions]
  );

  return {
    edit,
    editOpen,
    editSaving,
    editForm,
    setEditSaving,
    closeEdit,
    openEdit,
    paymentDrawerState: {
      open: paymentDrawerOpen,
      items: paymentDrawerItems,
      selectedItems: paymentDrawerSelectedItems,
      setOpen: setPaymentDrawerOpen,
      setSelectedItems: setPaymentDrawerSelectedItems
    },
    bulkPayState: {
      open: bulkPayOpen,
      form: bulkPayForm,
      saving: bulkPaySaving,
      setOpen: setBulkPayOpen,
      setSaving: setBulkPaySaving,
      contributions: bulkPayContributions,
      setContributions: setBulkPayContributions
    },
    table: {
      headerExtra,
      columnDefinitions,
      allColumnsMap,
      actionsColumn,
      rowSelection,
      onRowClickForSelection,
      pagination,
      request,
      onChange: sortContributions.onTableChange
    }
  };
}
